<template>
  <div class="admin-page admin-popup">
    <h1>전체목록</h1>

    <div class="menus">
      <button @click="$refs.createPopupDialog.load()" class="vhp-button">
        팝업 추가
      </button>
    </div>

    <vhp-table 
      :headers="headers"
      :items="popupList"
    >
      <!-- 이미지 -->
      <template #item-imageUrl="{ item }">
        <img
          :src="item.imageUrl"
          alt="팝업 이미지"
          class="popup-thumbnail pointer"
          @click="openImagePreview(item.imageUrl)"
        />
      </template>

      <!-- 링크 URL -->
      <template #item-linkUrl="{ item }">
        <a
          v-if="item.linkUrl"
          :href="item.linkUrl"
          target="_blank"
          class="link-url"
        >
          {{ item.linkUrl.length > 40 ? item.linkUrl.substring(0, 40) + '...' : item.linkUrl }}
        </a>
        <span v-else class="no-link">-</span>
      </template>

      <!-- 상태 -->
      <template #item-active="{ item }">
        <span :class="item.active ? 'status-active' : 'status-inactive'">
          {{ item.active ? '활성' : '비활성' }}
        </span>
      </template>

      <!-- 생성일 -->
      <template #item-createdDate="{ item }">
        {{ formatDate(item.createdDate) }}
      </template>

      <!-- 활성/비활성 토글 -->
      <template #item-toggle="{ item }">
        <button
          @click="toggleActive(item)"
          class="action-btn toggle-btn"
          :class="item.active ? 'btn-inactive' : 'btn-active'"
        >
          {{ item.active ? '비활성화' : '활성화' }}
        </button>
      </template>

      <!-- 삭제 -->
      <template #item-delete="{ item }">
        <img
          @click="deletePopup(item)"
          src="@/assets/image/trash-can-outline.svg"
          height="20"
          alt="삭제"
          class="pointer"
        />
      </template>
    </vhp-table>

    <!-- 팝업 생성 다이얼로그 -->
    <vhp-dialog
      title="팝업 추가"
      id="create-popup"
      class="create-dialog"
      ref="createPopupDialog"
      width="500"
      height="500"
    >
      <template v-slot:content>
        <div>
          <form @submit.prevent="createPopup" method="post" enctype="multipart/form-data">
            <label for="image">팝업 이미지 *</label>
            <div class="image-preview-container">
              <img
                v-if="newPopup.imagePreview"
                :src="newPopup.imagePreview"
                alt="이미지 미리보기"
                class="image-preview"
                @click="$refs.imageInput.click()"
              />
              <div
                v-else
                class="image-placeholder"
                @click="$refs.imageInput.click()"
              >
                <i class="fas fa-image" style="font-size: 48px; color: #ccc;"></i>
                <p>이미지를 클릭하여 선택하세요</p>
              </div>
              <input
                ref="imageInput"
                type="file"
                name="image"
                accept="image/*"
                @change="handleImageSelect"
                required
                hidden
              />
            </div>

            <label for="linkUrl">링크 URL</label>
            <input
              name="linkUrl"
              v-model="newPopup.linkUrl"
              placeholder="https://example.com (선택사항)"
              type="url"
              maxlength="500"
            />

            <button type="submit" class="save-btn vhp-button">추가</button>
          </form>
        </div>
      </template>
    </vhp-dialog>

    <!-- 이미지 미리보기 다이얼로그 -->
    <vhp-dialog
      title="이미지 미리보기"
      id="image-preview"
      ref="imagePreviewDialog"
      width="800"
    >
      <template v-slot:content>
        <img :src="previewImageUrl" alt="미리보기" style="max-width: 100%; height: auto;" />
      </template>
    </vhp-dialog>
  </div>
</template>

<script>
export default {
  name: "AdminPopup",
  data() {
    return {
      // 테이블 헤더
      headers: [
        { text: '이미지', value: 'imageUrl' },
        { text: '링크 URL', value: 'linkUrl' },
        { text: '상태', value: 'active' },
        { text: '생성일', value: 'createdDate' },
        { text: '활성/비활성', value: 'toggle' },
        { text: '삭제', value: 'delete' },
      ],
      // 데이터
      popupList: [],
      newPopup: {
        imageFile: null,
        imagePreview: null,
        linkUrl: "",
      },
      previewImageUrl: "",
    };
  },

  created() {
    this.getPopupList();   // 화면 로드시 자동 호출
  },

  methods: {
    // ✅ DB에서 목록 받아오기
    async getPopupList() {
      try {
        const res = await this.$axios.get("/popup/admin");
        this.popupList = res.data;   // ✅ 테이블에 바로 넣기
      } catch (err) {
        console.error("팝업 목록 조회 실패:", err);
        window.alert("팝업 목록을 불러올 수 없습니다.");
      }
    },

    handleImageSelect(e) {
      const file = e.target.files[0];
      if (file) {
        // 파일 크기 검증 (최대 5MB)
        if (file.size > 5 * 1024 * 1024) {
          window.alert("이미지 파일 크기는 5MB 이하여야 합니다.");
          e.target.value = "";
          return;
        }

        // 이미지 타입 검증
        if (!file.type.startsWith("image/")) {
          window.alert("이미지 파일만 업로드 가능합니다.");
          e.target.value = "";
          return;
        }

        this.newPopup.imageFile = file;

        // 미리보기 생성
        const reader = new FileReader();
        reader.onload = (e) => {
          this.newPopup.imagePreview = e.target.result;
        };
        reader.readAsDataURL(file);
      }
    },

    async createPopup() {
      if (!this.newPopup.imageFile) {
        window.alert("이미지를 선택해주세요.");
        return;
      }

      try {
        const formData = new FormData();
        formData.append("image", this.newPopup.imageFile);
        if (this.newPopup.linkUrl) {
          formData.append("linkUrl", this.newPopup.linkUrl);
        }

        await this.$axios.post("/popup/admin", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        window.alert("팝업이 추가되었습니다.");
        this.$refs.createPopupDialog.close();
        this.resetForm();
        this.getPopupList();
      } catch (err) {
        console.error("팝업 생성 실패:", err);
        if (err.response?.status === 400) {
          window.alert("이미지 파일이 필요합니다.");
        } else {
          window.alert("팝업 추가에 실패했습니다. 개발팀에 문의하세요.");
        }
      }
    },

    async toggleActive(item) {
      try {
        await this.$axios.put(`/popup/admin/${item.id}/active`);
        window.alert(
          `팝업이 ${item.active ? "비활성화" : "활성화"}되었습니다.`
        );
        this.getPopupList();
      } catch (err) {
        console.error("팝업 상태 변경 실패:", err);
        window.alert("팝업 상태 변경에 실패했습니다.");
      }
    },

    async deletePopup(item) {
      const res = window.confirm("정말 이 팝업을 삭제하시겠습니까?");
      if (!res) return;

      try {
        await this.$axios.delete(`/popup/admin/${item.id}`);
        window.alert("팝업이 삭제되었습니다.");
        this.getPopupList();
      } catch (err) {
        console.error("팝업 삭제 실패:", err);
        window.alert("팝업 삭제에 실패했습니다.");
      }
    },

    openImagePreview(imageUrl) {
      this.previewImageUrl = imageUrl;
      this.$refs.imagePreviewDialog.load();
    },

    resetForm() {
      this.newPopup = {
        imageFile: null,
        imagePreview: null,
        linkUrl: "",
      };
      if (this.$refs.imageInput) {
        this.$refs.imageInput.value = "";
      }
    },

    formatDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleString("ko-KR", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
  },
  mounted() {
    if (!this.$store.state.isAdmin) {
      alert("관리자 권한 없음");
      this.$router.push({ path: "/" });
    }
  },
};
</script>

<style lang="scss" src="./popup.scss"></style>
